import type { TFunction } from 'i18next'
import type { VoiceTemplateSourceType } from './voiceTemplateProvider'

/** Product V1 cap — matches backend ``CANONICAL_TEMPLATE_NAME_LIMIT`` (DB allows 256). */
export const CANONICAL_TEMPLATE_NAME_LIMIT = 100

export type VoiceTemplateFieldErrors = {
  templateName?: string
  voiceDescription?: string
  previewText?: string
  referenceAssetId?: string
  voiceRightsConfirmed?: string
}

export type VoiceTemplateValidationResult =
  | { ok: true; fieldErrors: VoiceTemplateFieldErrors }
  | { ok: false; fieldErrors: VoiceTemplateFieldErrors; firstInvalidField: keyof VoiceTemplateFieldErrors }

export function validateVoiceTemplateForm(
  args: {
    sourceType: VoiceTemplateSourceType
    voiceDescription: string
    previewText: string
    templateName: string
    referenceAssetId: string | null
    voiceRightsConfirmed: boolean
    hasLocalRecordingDraft?: boolean
  },
  t: TFunction<'canvas'>,
): VoiceTemplateValidationResult {
  const fieldErrors: VoiceTemplateFieldErrors = {}
  const name = args.templateName.trim()
  const preview = args.previewText.trim()
  const brief = args.voiceDescription.trim()

  if (!name) {
    fieldErrors.templateName = t('voiceTemplate.validation.templateNameRequired')
  } else if (name.length > CANONICAL_TEMPLATE_NAME_LIMIT) {
    fieldErrors.templateName = t('voiceTemplate.validation.templateNameTooLong', {
      max: CANONICAL_TEMPLATE_NAME_LIMIT,
    })
  }

  if (!preview) {
    fieldErrors.previewText = t('voiceTemplate.validation.previewTextRequired')
  }

  if (args.sourceType === 'AI_GENERATED' && !brief) {
    fieldErrors.voiceDescription = t('voiceTemplate.validation.voiceDescriptionRequired')
  }

  if ((args.sourceType === 'REFERENCE' || args.sourceType === 'CLONED') && !args.referenceAssetId) {
    fieldErrors.referenceAssetId = args.hasLocalRecordingDraft
      ? t('voiceTemplate.validation.referenceConfirmRequired')
      : t('voiceTemplate.validation.referenceRequired')
  }

  if (args.sourceType === 'CLONED' && !args.voiceRightsConfirmed) {
    fieldErrors.voiceRightsConfirmed = t('voiceTemplate.validation.consentRequired')
  }

  const keys = Object.keys(fieldErrors) as Array<keyof VoiceTemplateFieldErrors>
  if (keys.length === 0) {
    return { ok: true, fieldErrors }
  }
  return { ok: false, fieldErrors, firstInvalidField: keys[0]! }
}
